'use client';

import { useEffect, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { releaseReturnToAction } from '../api/releaseReturnToAction';
import type { RegisterFormValues } from '../model/types';
import { useInvitedEmail } from './useInvitedEmail';

/**
 * The invited email that locks the register form, and the way out when it is not the
 * person's. `release` resolves false when the return-to cookie could not be deleted;
 * the field then stays locked, because unlocking it would still send the person to
 * that invitation after signing up.
 */
export function useInvitedEmailLock(
  token: string | null | undefined,
  setValue: UseFormSetValue<RegisterFormValues>,
) {
  const [released, setReleased] = useState(false);
  const fetchedEmail = useInvitedEmail(token);
  const invitedEmail = released ? null : fetchedEmail;

  useEffect(() => {
    if (invitedEmail) setValue('email', invitedEmail);
  }, [invitedEmail, setValue]);

  async function release(): Promise<boolean> {
    try {
      await releaseReturnToAction();
    } catch {
      return false;
    }
    setReleased(true);
    setValue('email', '');
    return true;
  }

  return { invitedEmail, release };
}
